/**
 * Servicio de autenticación con Firebase
 * Maneja login, registro y gestión de sesión
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { auth } from './firebase';

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Iniciar sesión con email y contraseña
 */
export async function loginWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    const authError = error as { code: string; message: string };
    throw new Error(getAuthErrorMessage(authError.code));
  }
}

/**
 * Registrar nuevo usuario (se registra como user normal, luego se cambia a admin manualmente)
 */
export async function registerWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    const authError = error as { code: string; message: string };
    throw new Error(getAuthErrorMessage(authError.code));
  }
}

/**
 * Cerrar sesión
 */
export async function logout(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    const authError = error as { code: string; message: string };
    throw new Error(`Error al cerrar sesión: ${authError.message}`);
  }
}

/**
 * Obtener usuario actual
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Observar cambios en el estado de autenticación
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Obtener token de ID del usuario actual
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Obtener el rol del usuario desde los custom claims
 */
export async function getUserRole(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    // Obtener el token result que incluye los custom claims
    const tokenResult = await user.getIdTokenResult(true); // Force refresh para obtener claims actualizados
    return (tokenResult.claims.role as string) || null;
  } catch (error) {
    console.error('Error al obtener rol del usuario:', error);
    return null;
  }
}

/**
 * Traducir códigos de error de Firebase a mensajes en español
 */
function getAuthErrorMessage(code: string): string {
  const errorMessages: Record<string, string> = {
    'auth/invalid-email': 'El email no es válido',
    'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
    'auth/user-not-found': 'No existe una cuenta con este email',
    'auth/wrong-password': 'La contraseña es incorrecta',
    'auth/email-already-in-use': 'Este email ya está registrado',
    'auth/weak-password': 'La contraseña es muy débil (mínimo 6 caracteres)',
    'auth/network-request-failed': 'Error de conexión. Verifica tu internet',
    'auth/too-many-requests': 'Demasiados intentos fallidos. Intenta más tarde',
    'auth/operation-not-allowed': 'Esta operación no está permitida',
    'auth/invalid-credential': 'Credenciales inválidas',
  };

  return errorMessages[code] || 'Error al autenticar. Intenta nuevamente';
}

